import React, { useEffect, useState, useCallback } from 'react';
import { useUnderwritingConsent } from 'V2/Checkout/hooks/useUnderwritingConsent';
import { UWBlobSection } from './UWBlobSection';
import styles from '../styles.module.css';

interface UWConsentStepProps {
  appId: string;
  onAgreed: () => void;
}

export const UWConsentStep: React.FC<UWConsentStepProps> = ({
  appId,
  onAgreed,
}) => {
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const {
    content,
    isLoading,
    error,
    isAlreadyCompleted,
    fetch: fetchContent,
    accept,
  } = useUnderwritingConsent(appId);

  useEffect(() => {
    if (appId) void fetchContent();
  }, [appId, fetchContent]);

  // Auto-skip if no UW content or already completed
  useEffect(() => {
    if (!isLoading && !error && content !== undefined) {
      if (content === null || isAlreadyCompleted) {
        onAgreed();
      }
    }
  }, [isLoading, content, error, isAlreadyCompleted, onAgreed]);

  const handleAgree = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked || isAccepting) return;
      setIsAccepting(true);
      setAcceptError(null);
      try {
        await accept();
        onAgreed();
      } catch {
        setIsAccepting(false);
        setAcceptError('Unable to process your agreement. Please try again.');
      }
    },
    [accept, isAccepting, onAgreed]
  );

  return (
    <>
      <UWBlobSection
        isLoading={isLoading}
        error={error}
        content={content ?? null}
        isAccepting={isAccepting}
        acceptError={acceptError}
        onAgree={handleAgree}
        onFetchRetry={fetchContent}
      />
      {!isLoading && (
        <div className={styles.section2Locked}>
          <h2 className={styles.sectionTitle}>Complete your payment</h2>
          <p className={styles.section2Note}>
            Please complete the section above to continue.
          </p>
        </div>
      )}
    </>
  );
};
